import { Divider, Stack, Typography } from '@mui/material';
import config from 'config/default';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  isFormal: boolean;
  includeEmoji: boolean;
  swissS: boolean;
  language: string;
  secondaryLanguage: string;

  handleOpenFlowCreationDialog(): void;

  handleOpenChangeCommunicationStyleWarningDialog(): void;

  handleIncludeEmojiChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSwissSChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSecondaryLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const CommunicationSettings: React.FC<Props> = ({
  isFormal,
  includeEmoji,
  swissS,
  language,
  secondaryLanguage,
  handleOpenFlowCreationDialog,
  handleOpenChangeCommunicationStyleWarningDialog,
  handleIncludeEmojiChange,
  handleSwissSChange,
  handleLanguageChange,
  handleSecondaryLanguageChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack className={classes.communicationSettings} gap={3}>
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography className={classes.subTitle}>{t('selectCampaignFlowTemplate')}</Typography>
          <Typography className={classes.text}>{t('selectTemplateDescription')}</Typography>
        </Stack>
        <Stack direction={'row'} justifyContent={'center'}>
          <PaiButton text={t('showTemplateOptions')} size={'small'} border={'rounded'} action={handleOpenFlowCreationDialog} />
        </Stack>
        <Typography className={classes.errorText}>{t('templateOverwriteWarning')}</Typography>
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography className={classes.subTitle}>{t('language')}</Typography>
          <Typography className={classes.text}>{t('languageDescription')}</Typography>
        </Stack>
        <PaiSelect value={language ?? 'auto-detect'} onChange={handleLanguageChange} dense={true} style={{ maxWidth: '350px' }}>
          {config.textGenerationLanguages.map((lang, index) => (
            <PaiMenuItem key={index} value={lang.key ?? ''}>
              {lang.text}
            </PaiMenuItem>
          ))}
        </PaiSelect>
        <Typography className={classes.greyText}>{t('languageNote')}</Typography>

        {/* Secondary language dropdown */}
        <Stack gap={1} style={{ marginTop: 12 }}>
          <Typography className={classes.subTitle}>{t('secondaryLanguage')}</Typography>
          <Typography className={classes.text}>{t('secondaryLanguageDescription')}</Typography>
        </Stack>
        <PaiSelect
          value={secondaryLanguage || 'none'}
          onChange={handleSecondaryLanguageChange}
          dense={true}
          disabled={language === 'auto-detect'}
          style={{ maxWidth: '350px' }}
        >
          {/* Blank option */}
          <PaiMenuItem value={'none'}>{t('none')}</PaiMenuItem>
          {config.textGenerationLanguages
            .filter((lang) => lang.key !== 'auto-detect' && ['de', 'en'].includes(lang.key))
            .map((lang, index) => (
              <PaiMenuItem key={index} value={lang.key ?? ''} disabled={lang.key === language}>
                {lang.text}
              </PaiMenuItem>
            ))}
        </PaiSelect>

        {/* Validation message */}
        {secondaryLanguage && secondaryLanguage === language && (
          <Typography className={classes.errorText}>{t('secondaryLanguageMustDiffer')}</Typography>
        )}
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography className={classes.subTitle}>{t('communicationStyle')}</Typography>
          <Typography className={classes.text}>{t('communicationStyleDescription')}</Typography>
        </Stack>
        <Stack direction={'row'} gap={4}>
          <PaiCheckbox checked={isFormal} label={t('politeSpeech')} dense={true} onClick={handleOpenChangeCommunicationStyleWarningDialog} />
          <PaiCheckbox checked={includeEmoji} label={t('includeEmoji')} dense={true} onChange={handleIncludeEmojiChange} />
          <PaiCheckbox checked={swissS} label={t('convertSharpS')} dense={true} onChange={handleSwissSChange} />
        </Stack>
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  communicationSettings: {
    paddingBottom: '32px',
    marginBottom: '16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  errorText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.error.main,
  },
}));

export default CommunicationSettings;
